import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { Modal, Button } from "react-bootstrap";
import Breadcrumbs from "../../components/Breadcrumbs";
import { ToastContext } from "../../contexts/toast/toastContext";
import { ToastErrorContext } from "../../contexts/toast/toastErrorContext";
import { SweetAlertContext } from "../../contexts/sweetAlert/alertContext";
import { Form } from "@unform/web";
import { SubmitHandler, FormHandles } from "@unform/core";
import getValidationErrors from "../../helpers/getValidateErrors";
import { getSingleErrorMessage } from "../../helpers/getSingleErrorMessage";
import * as Yup from "yup";

import DataTable from "../../components/DataTable";
import DataNotFound from "../../components/DataNotFound";
import { IFormCores, IListCores } from "../../common/interfaces/cores.interfaces";
import { debounce } from "lodash";
import api from "../../services/api";
import { Input, Select } from "../../components/Form";
import { tipoMaterial, simOuNao } from "../../components/Form/Select/options";
import { toast } from "react-toastify";

const Cores: React.FC = () => {
  // Refs
  const formRef = useRef<FormHandles>(null);

  //Fields Typed
  const DataTableTS: any = DataTable;

  //Context
  const sweetAlertContext = useContext(SweetAlertContext);
  const toastContext = useContext(ToastContext);
  const toastErrorContext = useContext(ToastErrorContext);

  //States
  const [tableData, setTableData] = useState<IListCores[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [selected, setSelected] = useState<IFormCores>({} as IFormCores);
  const [novo, setNovo] = useState(false);
  const [reloadGrid, setReloadGrid] = useState(false);

  //Effects
  useEffect(() => {
    const fetchData = async () => {
      try {
        setTableLoading(true);
        const { data } = await api.get("cor/lista");
        setTableData(data);
      } catch (error: any) {
        toast.error(error.message);
      } finally {
        setTableLoading(false);
      }
    };

    fetchData();
  }, [reloadGrid]);

  //Handles
  const handleClose = () => setShow(false);

  const handleNew = () => {
    setNovo(true);
    setSelected({} as IFormCores);
    setShow(true);
  };

  const handleChangeFilter = useCallback(
    debounce(async (e) => {
      const { data } = await api.get(`cor/lista/${e.target.value}`);
      setTableData(data);
    }, 500),
    []
  );

  const handleEdit = useCallback(async ({ id }) => {
    const { data } = await api.get(`cor/${id}`);
    setSelected(data);
    setShow(true);
    setNovo(false);
  }, []);

  const handleDelete = ({ id, descricao }: IListCores) => {
    const title = `Excluir "${descricao}"?`;
    const message = "Essa cor será excluída, isso não pode ser desfeito.";
    sweetAlertContext.danger(title, message, async () => {
      try {
        await api.delete(`cor/${id}`);
        setTableData((prevState) =>
          prevState.filter(function (item) {
            return item.id !== id;
          })
        );
        sweetAlertContext.close();
      } catch (error) {
        const message = getSingleErrorMessage(error);
        if (message) {
          toastContext.notification(message, "warn");
        } else {
          toastContext.notification(
            "Ocorreu um erro ao realizar essa operação.",
            "error"
          );
        }
      }
    });
  };

  const handleSubmit: SubmitHandler = async (cor) => {
    try {
      formRef.current?.setErrors({});

      const message = "Campo obrigatório";
      const schema = Yup.object().shape({
        descricao: Yup.string().required(message).max(20, 'O nome da Cor só pode ter no máximo 20 caracteres.'),
        comum: Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable()
          .required(message),
        tipoMaterial: Yup.array().nullable().min(1, message).required(message),
      });

      await schema.validate(cor, { abortEarly: false });

      cor.id = selected.id;

      await api.post('cor', cor);

      setReloadGrid(reload => !reload);
      setShow(false);

      toast.success("A ação foi concluída.");
    } catch (err) {
      toastErrorContext.notification(err);
      formRef.current?.setErrors(getValidationErrors(err));
    }
  };

  const handleSave = () => {
    formRef.current?.submitForm();
    setNovo(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: "Cor",
        accessor: "descricao",
        display: "align-middle",
      },
      {
        Header: "Material",
        accessor: "tipoMaterial",
        display: "align-middle",
      },
      {
        Header: "Comum",
        accessor: "comum",
        display: "align-middle",
      },
      {
        Header: "Ação",
        accessor: "acao",
        disableSortBy: true,
        display: "align-middle column-table-2b",
        Cell: ({ row: { original } }: { row: { original: IListCores } }) => (
          <>
            <Button
              title="Editar"
              variant="ghost-dark"
              className="p-2"
              onClick={() => handleEdit(original)}
            >
              <i className="far fa-edit icon m-0"></i>
            </Button>
            <Button
              title="Excluir"
              variant="ghost-dark"
              className="p-2"
              onClick={() => handleDelete(original)}
            >
              <i className="far fa-trash-alt icon m-0"></i>
            </Button>
          </>
        ),
      },
    ],
    []
  );

  return (
    <>
      <div className="content">
        <div className="container-xl">
          <div className="page-header">
            <div className="row">
              <Breadcrumbs />
              <span className="col-auto ml-auto">
                <Button variant="white" onClick={() => handleNew()}>
                  Nova Cor
                </Button>
              </span>
              <div className="col-auto ml-0 pr-0">
                <div className="btn-list pr-0">
                  <span className="d-none d-sm-inline">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Busca..."
                      onChange={handleChangeFilter}
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="card p-0">
              <div className="card-body">
                <div className={'dimmer' + (tableLoading ? ' active' : '')}>
                  <div className="loader"></div>
                  <div className={"dimmer-content" + (tableLoading ? ' medium-box-loader' : '')} >
                    {tableData && (
                      <DataTableTS
                        columns={columns}
                        data={tableData}
                        bordered={false}
                        initialState={{
                          sortBy: [
                            {
                              id: "id",
                              desc: false,
                            },
                          ],
                        }}
                      />
                    )}
                    <DataNotFound
                      visible={
                        tableLoading == false ? tableData?.length === 0 : false
                      }
                    ></DataNotFound>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Adicionar Cor */}
      <Modal size="lg" show={show} onHide={handleClose} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{novo ? "Adicionar Cor" : "Editar Cor"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="mt-3" initialData={selected} ref={formRef} onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group col">
                <label className="form-label">Cor</label>
                <Input
                  type="text"
                  name="descricao"
                  className="form-control"
                  placeholder="Cor..."
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group col">
                <label className="form-label">
                  Selecione o tipo de materiais
                </label>
                <Select name="tipoMaterial" placeholder="Selecione uma opção..." options={tipoMaterial} defaultValue={selected.tipoMaterial} isMulti={true} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group col">
                <label className="form-label">Cor comum?</label>
                <Select
                  name="comum"
                  options={simOuNao}
                  placeholder="Selecione uma opção..."
                  defaultValue={selected.comum}
                ></Select>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Fechar
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Cores;
